import { frameId } from "../assets/manifest.ts";
import type { Command } from "../document/commands.ts";
import { EMPTY_TILE, unpackTile } from "../document/packed-tile.ts";
import type { LayerDocument, TilesetRef } from "../document/types.ts";
import type { Intent, LiveOp } from "../live/op-types.ts";
import { addPropFrames, type PlacedFramesContext } from "./placed-frames.ts";

/**
 * What a command, an intent or an operation brings that a tab may not hold (ADR-130 decisions 2.6 and 3.1), read from
 * the change alone, so a tab that receives an operation from another learns what to ask the library for with no
 * compile and no document. It yields the ids the frames route takes (decision 2.7): a frame id for a frame, and the
 * tileset's own id for a tileset added whole. A terrain layer's tiles are known only once its chunk paints, so a
 * stroke of corners brings none; the tab asks for them as the painter misses them.
 */

/**
 * What reading a change may need beyond the change: the one-shot strips a transition animation expands to, and the
 * world's tileset list, against which a map or a layer added with its cells reads them. A tile layer added with a
 * cell and no list is refused, since its cells name tilesets by position.
 */
export type IntroducedContext = Pick<PlacedFramesContext, "transitionStrips"> & { readonly tilesets?: readonly TilesetRef[] };

/**
 * One change of a document: a command, an intent or a whole operation.
 */
export type DocumentChange = Command | Intent | LiveOp;

/**
 * Adds the frames of a tile layer's cells, read against the world's tileset list.
 *
 * @param layer The layer; any layer but a tile layer adds nothing.
 * @param tilesets The world's tileset list, when the caller has it.
 * @param into The set to add to.
 * @throws Error when the layer holds a cell and there is no list, or a cell names a tileset past the list.
 */
function addTileLayerFrames(layer: LayerDocument, tilesets: readonly TilesetRef[] | undefined, into: Set<string>): void {
  if (layer.kind !== "tile") {
    return;
  }
  const seen = new Set<number>();
  for (const packed of layer.tiles) {
    if (packed === EMPTY_TILE || seen.has(packed)) {
      continue;
    }
    seen.add(packed);
    if (tilesets === undefined) {
      throw new Error(`layer ${layer.id} holds tiles, which name tilesets by position; the world's tilesets are needed to read them`);
    }
    const { tilesetIndex, tile } = unpackTile(packed);
    const tileset = tilesets[tilesetIndex];
    if (tileset === undefined) {
      throw new Error(`layer ${layer.id} names tileset index ${tilesetIndex}, but the world lists ${tilesets.length} tilesets`);
    }
    into.add(frameId(tileset.id, null, tile));
  }
}

/**
 * Adds the frames of a layer a command adds whole: a prop layer's props and a tile layer's cells.
 *
 * @param layer The layer.
 * @param context The context.
 * @param into The set to add to.
 */
function addLayerFrames(layer: LayerDocument, context: IntroducedContext, into: Set<string>): void {
  if (layer.kind === "prop") {
    for (const prop of layer.props) {
      addPropFrames(prop.frame, prop.behaviour, context.transitionStrips ?? {}, into);
    }
  } else {
    addTileLayerFrames(layer, context.tilesets, into);
  }
}

/**
 * Adds the frames a command brings.
 *
 * @param command The command.
 * @param context The context.
 * @param into The set to add to.
 * @throws Error when an added tile layer holds a cell and the context has no tileset list.
 */
function addCommandFrames(command: Command, context: IntroducedContext, into: Set<string>): void {
  const strips = context.transitionStrips ?? {};
  switch (command.kind) {
    case "compound":
      for (const part of command.commands) {
        addCommandFrames(part, context, into);
      }
      return;
    case "setTiles":
      for (const cell of command.cells) {
        if (cell.after !== null) {
          into.add(frameId(cell.after.tileset, null, cell.after.tile));
        }
      }
      return;
    case "fillCells":
      if (typeof command.after === "object" && command.after !== null) {
        into.add(frameId(command.after.tileset, null, command.after.tile));
      }
      return;
    case "addProp":
      addPropFrames(command.prop.frame, command.prop.behaviour, strips, into);
      return;
    case "updateProp":
      addPropFrames(command.after.frame, command.after.behaviour, strips, into);
      return;
    case "addLayer":
      addLayerFrames(command.layer, context, into);
      return;
    case "addMap":
      for (const layer of command.map.layers) {
        addLayerFrames(layer, context, into);
      }
      return;
    case "changeTilesets":
      for (const entry of command.insert) {
        if (entry.frames === undefined) {
          into.add(entry.id);
        } else {
          for (const tile of entry.frames) {
            into.add(frameId(entry.id, null, tile));
          }
        }
      }
      for (const set of command.frames ?? []) {
        if (set.after === undefined) {
          if (set.before !== undefined) {
            into.add(set.id);
          }
        } else {
          const held = new Set(set.before ?? set.after);
          for (const tile of set.after) {
            if (!held.has(tile)) {
              into.add(frameId(set.id, null, tile));
            }
          }
        }
      }
      return;
    default:
      return;
  }
}

/**
 * Adds the frames an intent brings.
 *
 * @param intent The intent.
 * @param context The context.
 * @param into The set to add to.
 */
function addIntentFrames(intent: Intent, context: IntroducedContext, into: Set<string>): void {
  const strips = context.transitionStrips ?? {};
  switch (intent.k) {
    case "cells":
      if (intent.grid === "tiles") {
        for (const value of intent.palette) {
          if (typeof value === "object" && value !== null) {
            into.add(frameId(value.tileset, null, value.tile));
          }
        }
      }
      return;
    case "addProp":
      addPropFrames(intent.prop.frame, intent.prop.behaviour, strips, into);
      return;
    case "updateProp":
      addPropFrames(intent.frame, intent.behaviour ?? undefined, strips, into);
      return;
    case "changeTilesets":
      for (const id of intent.add) {
        into.add(id);
      }
      for (const pulled of intent.pull ?? []) {
        for (const tile of pulled.tiles) {
          into.add(frameId(pulled.id, null, tile));
        }
      }
      return;
    default:
      return;
  }
}

/**
 * Lists the ids a change brings that a tab may not hold: a cell patch's tiles (its palette), an added prop's frame,
 * state frames and animation strips, an updated prop's new frame and behaviour, a set of tilesets' pulls and adds
 * (a frame id for a pulled or partly listed tile, the tileset's id for one added whole), the props and tiles of a
 * layer or a map a command adds, and a compound's or an operation's parts. What a change removes, renames or moves
 * brings nothing. The caller subtracts what it holds.
 *
 * @param change A command, an intent or an operation.
 * @param context The strips and the tileset list the change may need; none by default.
 * @returns Every id once, in code-unit order.
 * @throws Error when a command adds a tile layer holding a cell and the context has no tileset list, or a cell names
 *   a tileset past it.
 */
export function framesIntroduced(change: DocumentChange, context: IntroducedContext = {}): string[] {
  const ids = new Set<string>();
  if ("list" in change) {
    for (const intent of change.list) {
      addIntentFrames(intent, context, ids);
    }
  } else if ("k" in change) {
    addIntentFrames(change, context, ids);
  } else {
    addCommandFrames(change, context, ids);
  }
  return [...ids].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}
